// Copyright (c) 2025-2026 NacenCheck — TracePro Compliance-Grade Trace OS.
// Licensed under the MIT License — see LICENSE file in the repo root.
// SPDX-License-Identifier: MIT

"use client";

/**
 * ApiErrorBridge — Phase 54 W4.5
 *
 * Mounted once at the root layout. Subscribes to the api-client error bus and
 * translates 401/403/429/5xx into router push + toast in this Next.js app.
 *
 * Wiring is intentionally thin so we can keep `useApiErrorHandler` itself free
 * of @tracepro/ui or next/navigation deps (avoids hooks → ui circular dep).
 */

import { useRouter } from "next/navigation";
import { useApiErrorHandler } from "@tracepro/hooks";
import { useToast } from "@tracepro/ui";
import { stripBase } from "@tracepro/mobile-host/base-path";

export function ApiErrorBridge() {
  const router = useRouter();
  const { toast } = useToast();

  useApiErrorHandler({
    onUnauthorized: () => {
      const path = stripBase(window.location.pathname);
      if (path === "/sign-in") return;
      const returnTo = path + window.location.search + window.location.hash;
      router.replace(`/sign-in?redirect_url=${encodeURIComponent(returnTo)}`);
    },
    // API 403 stays on the current page so failed actions do not discard work.
    onForbidden: () =>
      toast({
        title: "Không có quyền",
        description: "Bạn không có quyền thực hiện thao tác này.",
        variant: "destructive",
      }),
    // 2026-05-21 — UC-WF-04 server-side 403 ack-required. Emit a window event
    // so the dashboard's WorkflowAckBanner refreshes its pending list. We
    // intentionally do NOT navigate here — the banner sits on the dashboard
    // layout and just needs an immediate refresh.
    onWorkflowAckRequired: () => {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("tracepro:workflow-ack-required"));
      }
    },
    onToast: ({ title, description, variant, duration }) =>
      toast({ title, description, variant, duration }),
  });

  return null;
}
